/**
 * Sandbox (docs/SCENARIOS.md).
 *
 * Choose season, seed, events and difficulty; run anywhere from a day to a year with seeded
 * weather. There is no pass condition — the score card still computes, but nothing unlocks.
 *
 * `makeSandbox` builds a scenario config from the launcher's choices. Weather keyframes are
 * generated from a seasonal climatology rather than hand-authored, so a year-long run has a real
 * annual cycle: cold wet winters, a spring freshet driven by the snow that accumulated during
 * them, and a dry late summer.
 */

import type { ScenarioConfig } from '../engine/types';

export interface SandboxOptions {
  siteId: string;
  /** Month to start in, 1–12. */
  startMonth: number;
  durationDays: number;
  /** Initial pool elevation, m ASL. */
  headwater: number;
  wet: number;
}

/** Monthly climatology for the Sable basin: mean valley temperature °C and precipitation mm/h. */
const CLIMATE_T = [-2, 0, 3, 7, 11, 14, 17, 17, 13, 8, 3, -1];
const CLIMATE_P = [0.09, 0.08, 0.07, 0.05, 0.04, 0.03, 0.02, 0.02, 0.04, 0.07, 0.11, 0.1];

/** Typical SWE by band at the start of each month, mm — the pack the freshet later delivers. */
const CLIMATE_SWE: number[][] = [
  [120, 380, 620], [160, 470, 740], [170, 540, 850], [90, 500, 880],
  [20, 380, 780], [0, 150, 520], [0, 20, 260], [0, 0, 110],
  [0, 0, 60], [0, 0, 40], [30, 90, 180], [80, 240, 400],
];

export function makeSandbox(o: SandboxOptions): ScenarioConfig {
  const month0 = Math.max(0, Math.min(11, o.startMonth - 1));
  const durationH = Math.max(24, o.durationDays * 24);
  const year = 2026;
  const start = `${year}-${String(month0 + 1).padStart(2, '0')}-01T00:00`;

  // One keyframe per week, walking the climatology forward from the start month.
  const weather: ScenarioConfig['weather'] = [];
  for (let h = 0; h <= durationH; h += 168) {
    const monthOffset = Math.floor(h / 730);
    const m = (month0 + monthOffset) % 12;
    const next = (m + 1) % 12;
    const frac = (h / 730) - monthOffset;
    const t = (CLIMATE_T[m] ?? 8) * (1 - frac) + (CLIMATE_T[next] ?? 8) * frac;
    const p = (CLIMATE_P[m] ?? 0.05) * (1 - frac) + (CLIMATE_P[next] ?? 0.05) * frac;
    weather.push({ h, tempC: t, precipMmH: p * (0.6 + 0.8 * o.wet) });
  }

  return {
    id: 'sandbox',
    index: 99,
    name: 'Sandbox',
    siteId: o.siteId,
    start,
    durationH,
    teaches: 'Whatever you point it at.',
    briefing:
      'Free operation. Seeded weather from the basin climatology, no objectives, no unlock. ' +
      'Run it for a day to try a gate sequence, or for a year to watch a full annual cycle: ' +
      'drawdown through the winter, refill on the freshet, and the long dry recession into autumn.',
    initial: {
      headwater: o.headwater,
      swe: (CLIMATE_SWE[month0] ?? [0, 200, 500]).slice(),
      baseflow: 420,
      wetness: 0.4,
      unitsOnline: [0, 1],
      initialSetpointMW: 180,
    },
    weather,
    priceOverlays: [],
    events: [],
    hazardRate: 1.0,
    forecastBias: 0,
    forecastBiasDecayH: 0,
    objectives: [],
    debriefHints: [],
    gradeThresholds: [1e12, 1e12, 1e12, 1e12],
  };
}

export const sandbox: ScenarioConfig = makeSandbox({
  siteId: 'kettle-narrows',
  startMonth: 5,
  durationDays: 30,
  headwater: 735,
  wet: 0.5,
});
